import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  AccountTree as AccountTreeIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

/** Clave del programa para validar permisos (roles / menú). */
export const PROGRAMA_GESTION_CENTROS_DE_COSTO = 'gestioncentrosdecosto';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const formularioVacio = {
  id: null,
  descrip: '',
};

/**
 * GestionCentrosDeCosto: CRUD de centros de costo.
 * Seguridad y diseño alineados a FacturaPOS (contexto + permisosPorModulo + FinancePro).
 */
const GestionCentrosDeCosto = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_GESTION_CENTROS_DE_COSTO),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_GESTION_CENTROS_DE_COSTO),
    [roleDetalles]
  );
  const puedeEliminar = puedeModificar;

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [form, setForm] = useState(formularioVacio);
  const [guardando, setGuardando] = useState(false);
  const [eliminarId, setEliminarId] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const credencialesBase = useCallback(
    () => ({
      usuario: usuarioLog || '',
      password: claveLog || '',
      id_empresa: selectedCompany,
    }),
    [usuarioLog, claveLog, selectedCompany]
  );

  const cargarLista = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setFilas([]);
      return;
    }
    setCargando(true);
    try {
      const res = await fetch(servidor + '/api/configuracionbackend_listarcentrosdecosto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credencialesBase()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al cargar centros de costo');
      }
      setFilas(Array.isArray(data.centros) ? data.centros : []);
    } catch (e) {
      console.error(e);
      showError(e.message || 'Error al cargar centros de costo');
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, puedeOperar, servidor, credencialesBase, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargarLista();
  }, [puedeLeer, puedeOperar, cargarLista]);

  const abrirNuevo = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear registros');
      return;
    }
    setForm(formularioVacio);
    setDialogoAbierto(true);
  };

  const abrirEditar = (row) => {
    if (!puedeModificar) {
      showError('No tiene permisos para modificar registros');
      return;
    }
    setForm({
      id: row.id,
      descrip: row.descrip || '',
    });
    setDialogoAbierto(true);
  };

  const cerrarDialogo = () => {
    if (guardando) return;
    setDialogoAbierto(false);
    setForm(formularioVacio);
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar');
      return;
    }
    const descrip = (form.descrip || '').trim();
    if (!descrip) {
      showError('El nombre del centro de costo es obligatorio');
      return;
    }
    setGuardando(true);
    try {
      const body = {
        ...credencialesBase(),
        descrip,
      };
      if (form.id != null) {
        body.id = form.id;
      }
      const res = await fetch(servidor + '/api/configuracionbackend_guardarcentrodecosto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al guardar');
      }
      showSuccess(form.id != null ? 'Registro actualizado' : 'Registro creado');
      setDialogoAbierto(false);
      setForm(formularioVacio);
      cargarLista();
    } catch (e) {
      showError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!puedeEliminar) {
      showError('No tiene permisos para eliminar');
      return;
    }
    if (eliminarId == null) return;
    setEliminando(true);
    try {
      const res = await fetch(servidor + '/api/configuracionbackend_eliminarcentrodecosto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          id: eliminarId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al eliminar');
      }
      showSuccess('Registro eliminado');
      setEliminarId(null);
      cargarLista();
    } catch (e) {
      showError(e.message || 'Error al eliminar');
    } finally {
      setEliminando(false);
    }
  };

  const botonNavyPrimarioSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 1.4,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const botonOutlinedSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 1.4,
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const bodyCellSx = {
    fontSize: '0.86rem',
    color: FP.color.textPrimary,
    borderBottom: `1px solid ${FP.color.border}`,
  };

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Centros de costo"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Configuración contable`
            : 'Configuración contable'
        }
      />
      <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
            {mensaje}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('No tienes permisos para acceder a la gestión de centros de costo.');
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Centros de costo"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Configuración contable`
            : 'Configuración contable'
        }
      />

      <Card sx={{ ...cardShellSx, mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.4,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1rem' }}>
                Centros de costo
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                El código es el identificador numérico en base de datos; al crear un registro solo
                indique el nombre.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={cargarLista}
                disabled={!puedeOperar || cargando}
                sx={botonOutlinedSx}
              >
                Actualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={abrirNuevo}
                disabled={!puedeOperar || !puedeModificar}
                sx={botonNavyPrimarioSx}
              >
                Nuevo centro de costo
              </Button>
            </Box>
          </Box>

          {!puedeOperar && (
            <Box
              sx={{
                p: 1.4,
                mb: 2,
                borderRadius: 1.4,
                bgcolor: FP.color.infoSoft,
                color: FP.color.info,
                border: `1px solid ${FP.color.info}33`,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Seleccione una empresa con base de datos y asegúrese de tener sesión iniciada para
              administrar centros de costo.
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              maxHeight: 'calc(100vh - 320px)',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell sx={{ width: 120 }}>
                    <Typography component="span" sx={cellHeadSx}>
                      Código
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Nombre
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    <Typography
                      component="span"
                      sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                    >
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {cargando && filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ ...bodyCellSx, py: 4 }}>
                      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
                    </TableCell>
                  </TableRow>
                ) : filas.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{ ...bodyCellSx, color: FP.color.textSecondary, py: 4 }}
                    >
                      No hay registros. Cree uno con &quot;Nuevo centro de costo&quot;.
                    </TableCell>
                  </TableRow>
                ) : (
                  filas.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.id}</TableCell>
                      <TableCell sx={bodyCellSx}>{r.descrip || '—'}</TableCell>
                      <TableCell align="right" sx={bodyCellSx}>
                        <Tooltip title={puedeModificar ? 'Editar' : 'Sin permiso para modificar'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => abrirEditar(r)}
                              disabled={!puedeModificar}
                              sx={{
                                color: FP.color.accentBlue,
                                '&:hover': { bgcolor: FP.color.accentBlueSoft },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            puedeEliminar
                              ? 'Eliminar (bloqueado si el centro está usado en el diario)'
                              : 'Sin permiso para eliminar'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setEliminarId(r.id)}
                              disabled={!puedeEliminar}
                              sx={{
                                color: FP.color.danger,
                                '&:hover': { bgcolor: FP.color.dangerSoft },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={dialogoAbierto}
        onClose={cerrarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              {form.id != null ? 'Editar centro de costo' : 'Nuevo centro de costo'}
            </Typography>
          </Box>
          <IconButton
            onClick={cerrarDialogo}
            size="small"
            disabled={guardando}
            sx={{ color: FP.color.textSecondary }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, md: 3 },
            pb: { xs: 2, md: 3 },
            pt: { xs: '20px !important', md: '24px !important' },
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
          }}
        >
          {form.id != null && (
            <TextField
              fullWidth
              label="Código"
              value={String(form.id)}
              disabled
              helperText="Asignado por el sistema; no se puede cambiar."
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          )}
          <TextField
            required
            fullWidth
            label="Nombre del centro de costo"
            value={form.descrip}
            onChange={(e) => setForm((p) => ({ ...p, descrip: e.target.value }))}
            placeholder="Ej. Administración, Producción, Ventas…"
            inputProps={{ maxLength: 50 }}
            helperText={
              form.id != null
                ? 'Hasta 50 caracteres.'
                : 'El código se generará automáticamente al guardar. Hasta 50 caracteres.'
            }
            disabled={!puedeModificar}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogo}
            startIcon={<CloseIcon />}
            disabled={guardando}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando || !puedeModificar || !puedeOperar}
            sx={botonNavyPrimarioSx}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={eliminarId != null}
        onClose={() => !eliminando && setEliminarId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Eliminar centro de costo
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
            ¿Confirma eliminar este registro? Si el centro está usado en asientos del diario
            contable, el sistema impedirá borrarlo.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={() => setEliminarId(null)}
            disabled={eliminando}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
            }
            onClick={confirmarEliminar}
            disabled={eliminando || !puedeEliminar}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              bgcolor: FP.color.danger,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C3C', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {eliminando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionCentrosDeCosto;
